import React, { useState, useEffect, useCallback } from "react";
import { Outlet, NavLink, useNavigate, Navigate } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import Swal from "sweetalert2";

import {
  FiGrid,
  FiUsers,
  FiVideo,
  FiBriefcase,
  FiCheckCircle,
  FiBell,
  FiLogOut,
  FiSettings,
  FiDatabase,
  FiFileText,
  FiDollarSign,
  FiTag,
  FiMessageSquare,
  FiList,
  FiTerminal,
  FiActivity,
  FiCpu,
  FiUserCheck,
  FiCreditCard,
  FiTrendingUp,
  FiX
} from "react-icons/fi";

import "./AdminLayout.css";

/* =========================================
   PROTECTED ROUTE
// ========================================= */
// export const AdminProtectedRoute = ({ children }) => {
//   const token = localStorage.getItem("adminToken");

//   if (!token) {
//     return <Navigate to="/admin/login" replace />;
//   }

//   return children;
// };
export const AdminProtectedRoute = ({ children }) => {
  const token = localStorage.getItem("adminToken");

  if (!token) {
    return <Navigate to="/admin/login" replace />;
  }

  // Ensure session flag is set for current tab if valid token exists
  if (!sessionStorage.getItem("adminSessionActive")) {
    sessionStorage.setItem("adminSessionActive", "true");
  }

  return children;
};

/* =========================================
   ADMIN LAYOUT
========================================= */
// const AdminLayout = () => {
//   const navigate = useNavigate();

//   const handleLogout = () => {
//     localStorage.removeItem("adminToken");
//     navigate("/admin/login");
//   };

//   useEffect(() => {
//     document.body.style.paddingTop = '0px';
//     return () => {
//       document.body.style.paddingTop = '63px';
//     };
//   }, []);
const AdminLayout = () => {
  const navigate = useNavigate();
  const [showNotif, setShowNotif] = useState(false);
  const [notifications, setNotifications] = useState([
    
  ]);

  const handleLogout = useCallback((reason = "") => {
    localStorage.removeItem("adminToken");
    sessionStorage.removeItem("adminSessionActive");

    if (reason) {
      Swal.fire({
        icon: "warning",
        title: "Session Expired",
        text: reason,
        showConfirmButton: true,
        confirmButtonText: "OK",
        confirmButtonColor: "#d33",
        allowOutsideClick: false,
        background: "#111827",
        color: "#fff",
        timer: 8000,
      }).then(() => {
        navigate("/admin/login", { replace: true });
      });
    } else {
      navigate("/admin/login", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const INACTIVITY_LIMIT = 30 * 60 * 1000;
    let idleTimer;

    const resetIdleTimer = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        handleLogout("You have been automatically logged out due to system inactivity. Please log in again.");
      }, INACTIVITY_LIMIT);
    };

    const activityEvents = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    activityEvents.forEach((event) => window.addEventListener(event, resetIdleTimer));
    resetIdleTimer();

    return () => {
      if (idleTimer) clearTimeout(idleTimer);
      activityEvents.forEach((event) => window.removeEventListener(event, resetIdleTimer));
    };
  }, [handleLogout]);

  useEffect(() => {
    document.body.style.paddingTop = '0px';
    return () => {
      document.body.style.paddingTop = '63px';
    };
  }, []);

  return (
    <div className="admin-layout">
      {/* TOP HEADER */}
      <header className="admin-topbar">
        <div className="topbar-left">
          <div className="d-flex align-items-center gap-3">
            <div className="brand-logo-box">B</div>
            <h5 className="mb-0 fw-bold">BMA Admin</h5>
          </div>
        </div>

        <div className="topbar-actions">
          {/* 🔔 INTERACTIVE NOTIFICATION BELL DROPDOWN */}
          <Dropdown align="end" show={showNotif} onToggle={(isOpen) => setShowNotif(isOpen)}>
            <Dropdown.Toggle as="div" className="notification-bell">
              <FiBell />
              {notifications.length > 0 && (
                <span className="notification-badge">{notifications.length}</span>
              )}
            </Dropdown.Toggle>

            <Dropdown.Menu
              variant="dark"
              className="admin-notification-dropdown shadow-lg mt-2 p-0"
            >
              <div className="admin-notif-header">
                <div className="d-flex align-items-center gap-3">
                  <div className="admin-notif-bell-box">
                    <span>🔔</span>
                  </div>
                  <div>
                    <h6 className="admin-notif-title">Notifications</h6>
                    <span className="admin-notif-subtitle">{notifications.length} new updates</span>
                  </div>
                </div>
                <button
                  type="button"
                  className="admin-notif-close-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowNotif(false);
                  }}
                  title="Close"
                >
                  <FiX size={18} />
                </button>
              </div>

              <div className="admin-notification-scroll">
                {notifications.map((n) => (
                  <Dropdown.Item
                    key={n.id}
                    className="admin-notif-item"
                    onClick={() => {
                      setShowNotif(false);
                      navigate(n.link);
                    }}
                  >
                    <div className="d-flex gap-3 align-items-start">
                      <div className="admin-notif-item-icon">
                        {n.icon || "🔔"}
                      </div>
                      <div className="flex-grow-1">
                        <div className="admin-notif-item-title">{n.title}</div>
                        <div className="admin-notif-item-desc">{n.desc}</div>
                        <div className="admin-notif-item-time">{n.time}</div>
                      </div>
                    </div>
                  </Dropdown.Item>
                ))}
              </div>

              <div className="admin-notif-footer text-center p-2">
                <NavLink
                  to="/admin/notification-logs"
                  className="admin-notif-footer-link"
                  onClick={() => setShowNotif(false)}
                >
                  View All Notification Logs →
                </NavLink>
              </div>
            </Dropdown.Menu>
          </Dropdown>

          <Dropdown align="end">
            <Dropdown.Toggle as="div" className="user-profile">
              <div className="text-end d-none d-md-block">
                <div className="welcome-text">WELCOME</div>
                <div className="admin-name">ADMIN</div>
              </div>
              <div className="avatar-circle">A</div>
            </Dropdown.Toggle>

            <Dropdown.Menu
              variant="dark"
              className="shadow-lg border-0 mt-2 p-2"
              style={{ minWidth: "220px" }}
            >
              <div className="px-3 py-2 border-bottom border-secondary mb-2">
                <small className="text-muted d-block">Logged in as</small>
                <strong>BMA Admin</strong>
              </div>

              <Dropdown.Item className="rounded">
                <FiSettings className="me-2" />
                Settings
              </Dropdown.Item>

              <Dropdown.Item
                onClick={() => handleLogout()}
                className="text-danger rounded d-flex align-items-center"
              >
                <FiLogOut className="me-2" />
                Logout
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </header>

      {/* BODY */}
      <div className="admin-body">
        {/* SIDEBAR */}
        <aside className="admin-sidebar">
          <div className="sidebar-scroll">
            <ul className="sidebar-nav">
              <li>
                <NavLink to="/admin/dashboard" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                  <FiGrid />
                  <span>Dashboard</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/candidates" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                  <FiUsers />
                  <span>Candidates</span>
                </NavLink>
              </li>

              <li>
                <NavLink to="/admin/casting-directors" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                  <FiUsers />
                  <span>Casting Directors</span>
                </NavLink>
              </li>

              <li>
                <NavLink to="/admin/directors" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                  <FiVideo />
                  <span>Directors / Producers</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/projects" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                  <FiBriefcase />
                  <span>Projects</span>
                </NavLink>
              </li>

              <li>
                <NavLink to="/admin/roles" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                  <FiCheckCircle />
                  <span>Roles</span>
                </NavLink>
              </li>
              

              <li>
                <NavLink to="/admin/audition-list" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                  <FiList />
                  <span>Audition List</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/bank-setup" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                  <FiCreditCard />
                  <span>CD Bank Details</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/contact-list" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                  <FiMessageSquare />
                  <span>Contact List</span>
                </NavLink>
              </li>
              <li className="menu-title"><hr/></li>

              {/* ✅ LEAD MANAGEMENT LINK ADDED HERE */}
              <li>
                <NavLink to="/admin/leads" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                  <FiUserCheck />
                  <span>Lead Management</span>
                </NavLink>
              </li>

              <li>
                <NavLink to="/admin/notifications" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                  <FiBell />
                  <span>Notification Templates</span>
                </NavLink>
              </li>

              <li>
                <NavLink to="/admin/notification-logs" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                  <FiFileText />
                  <span>Notification Logs</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/cost-page" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                  <FiDollarSign />
                  <span>Cost Pricing</span>
                </NavLink>
              </li>

              <li>
                <NavLink to="/admin/coupon-redeem" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                  <FiTag />
                  <span>Coupons</span>
                </NavLink>
              </li>

              <li>
                <NavLink to="/admin/lookups" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                  <FiMessageSquare />
                  <span>Lookup Management</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/payment-gateway" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                  <FiMessageSquare />
                  <span>Payment Gateway</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/settlement" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                  <FiCreditCard />
                  <span>Payout Settlements</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/finance" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                  <FiTrendingUp />
                  <span>Income & Expenses (P&L)</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/payment-logs" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                  <FiDollarSign />
                  <span>Payment Logs</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/payment-reconciliation" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                  <FiActivity />
                  <span>Payment Reconciliation</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/user-activity-logs" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                  <FiCpu />
                  <span>System & Activity Logs</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/webhook-logs" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                  <FiTerminal />
                  <span>Webhook Logs</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/testimonial" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                  <FiMessageSquare />
                  <span>Testimonials</span>
                </NavLink>
              </li>
              
              <li>
                <NavLink to="/admin/migration" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                  <FiDatabase />
                  <span>Migration</span>
                </NavLink>
              </li> 
            </ul>
          </div>
        </aside>

        {/* MAIN CONTENT */}
        <main className="admin-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;